/**
 * Reusable confirmation dialog for destructive / affirmative actions.
 * API mirrors haat-hub's ConfirmModal so the component can drop into any
 * Partner Connect feature.
 */

import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  Trash2,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ConfirmModalVariant = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  description?: string;
  variant?: ConfirmModalVariant;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  icon?: ReactNode;
  showWarning?: boolean;
  warningMessage?: string;
}

interface VariantStyle {
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  confirmBtn: string;
  warningBg: string;
  warningBorder: string;
  warningText: string;
}

const variantConfig: Record<ConfirmModalVariant, VariantStyle> = {
  danger: {
    icon: <Trash2 size={26} />,
    iconBg: 'bg-red-100',
    iconColor: 'text-[#c00035]',
    confirmBtn:
      'bg-[#c00035] hover:bg-[#a3002d] text-white focus:ring-[#c00035]/40',
    warningBg: 'bg-red-50',
    warningBorder: 'border-red-200',
    warningText: 'text-[#c00035]',
  },
  warning: {
    icon: <AlertTriangle size={26} />,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-500',
    confirmBtn:
      'bg-orange-500 hover:bg-orange-600 text-white focus:ring-orange-400/40',
    warningBg: 'bg-orange-50',
    warningBorder: 'border-orange-200',
    warningText: 'text-orange-700',
  },
  info: {
    icon: <Info size={26} />,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    confirmBtn:
      'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400/40',
    warningBg: 'bg-blue-50',
    warningBorder: 'border-blue-200',
    warningText: 'text-blue-700',
  },
  success: {
    icon: <CheckCircle size={26} />,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-500',
    confirmBtn:
      'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-400/40',
    warningBg: 'bg-emerald-50',
    warningBorder: 'border-emerald-200',
    warningText: 'text-emerald-700',
  },
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  description,
  variant = 'danger',
  confirmText,
  cancelText,
  isLoading = false,
  icon,
  showWarning = true,
  warningMessage,
}) => {
  const { t } = useTranslation();
  const styles = variantConfig[variant];
  const displayIcon = icon ?? styles.icon;

  const defaultWarning =
    variant === 'danger' ? (t('common.delete.cannotBeUndone') as string) : undefined;
  const finalWarning = warningMessage ?? defaultWarning;

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Close on ESC.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !isLoading && onClose()}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
          >
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <h2
                id="confirm-modal-title"
                className="text-base sm:text-lg font-semibold text-gray-900 truncate pe-3"
              >
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                aria-label="close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 sm:px-6 py-5 space-y-5">
              <div className="text-center">
                <div
                  className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${styles.iconBg}`}
                >
                  <div className={styles.iconColor}>{displayIcon}</div>
                </div>
                <div className="text-gray-700">
                  {typeof message === 'string' ? <p>{message}</p> : message}
                </div>
                {description && (
                  <p className="text-sm text-gray-500 mt-2 break-words">
                    {description}
                  </p>
                )}
              </div>

              {showWarning && finalWarning && (
                <div
                  className={`p-3 rounded-xl border text-sm ${styles.warningBg} ${styles.warningBorder} ${styles.warningText}`}
                >
                  {finalWarning}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {cancelText ?? (t('common.cancel') as string)}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-sm focus:outline-none focus:ring-4 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${styles.confirmBtn}`}
                >
                  {isLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : variant === 'danger' ? (
                    <Trash2 size={16} />
                  ) : null}
                  <span>
                    {confirmText ??
                      (variant === 'danger'
                        ? (t('common.delete.confirm') as string)
                        : (t('common.yes') as string))}
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ConfirmModal;
